"use client";
import { commaSeparateNumber } from "@highway/mbs-utils";
import { FormHelperText, OutlinedInput, Stack, Typography, useTheme } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import { useEffect, useRef, useState } from "react";
import { IHInput, IInputWithDefaultProps, useValidation } from ".";
import { HButton } from "..";
import { formatPhone, toPhoneDigits } from "../../utilities/formatters";
import { IButtonProps } from "../Button/Button";

const HInput = ({
    divStyle = {},
    helpLabel,
    label,
    value = "",
    optional,
    focused,
    type,
    onChange,
    validation,
    warning,
    maxDecimals = 0,
    defaultEmpty,
    tag,
    labelButton,
    maxCharacters,
    ...props
}: IHInput) => {
    const componentRef = useRef<HTMLDivElement>(null);
    const [visibleValue, setVisibleValue] = useState(() => {
        if (type === "number") return defaultEmpty ? "" : `${commaSeparateNumber(value, maxDecimals)}`;
        if (type === "tel") return formatPhone(value);
        return `${value}`;
    });
    const [isFocused, setIsFocused] = useState(false);
    const { error, errorMessage } = useValidation({
        value,
        isFocused,
        validation,
        tag,
        ref: componentRef,
    });
    const hasError = error || !!warning;
    const message = error ? errorMessage : warning;

    useEffect(() => {
        if (
            type !== "number" ||
            (defaultEmpty && visibleValue === "") ||
            +commaSeparateNumber(value, maxDecimals).replaceAll(",", "") !== +visibleValue.replaceAll(",", "")
        ) {
            formatVisible(value);
        }
    }, [value]);

    const handleFocus = () => {
        if (type === "number" && value === 0) setVisibleValue("");
        focused?.(true);
        setIsFocused(true);
    };
    const handleBlur = () => {
        if (type === "number") formatVisible(value);
        focused?.(false);
        setIsFocused(false);
    };

    const formatVisible = (val: string | number) => {
        let visible = val;
        if (type === "number" && val !== "") visible = commaSeparateNumber(val, maxDecimals);
        if (type === "tel") visible = formatPhone(val);
        setVisibleValue(`${visible}`);
    };

    const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value;
        if (type === "number") {
            val = e.target.value.replace(/[^0-9.]/g, "");
            const [integer, decimals] = val.split(".");
            // Making sure there is only the allowed decimal places
            const hasDecimal = decimals !== undefined;
            val = integer + (hasDecimal ? `.${decimals.slice(0, maxDecimals)}` : "");
            if (maxCharacters && integer.length + (decimals?.length || 0) > maxCharacters) return;
            if (val === ".") val = "0.";
            let settings;
            if (+val !== value && !isNaN(+val)) {
                settings = onChange?.({ target: { value: val } });
            }
            if (!settings || !settings.preventCacheUpdate) formatVisible(val);
        } else if (type === "tel") {
            val = toPhoneDigits(val);
            const settings = onChange?.({ target: { value: val } });
            if (!settings || !settings.preventCacheUpdate) formatVisible(val);
        } else {
            if (maxCharacters && val.length > maxCharacters) return;
            const settings = onChange?.({ target: { value: val } });
            if (!settings || !settings.preventCacheUpdate) formatVisible(val);
        }
    };

    return (
        <Stack ref={componentRef} spacing={1} style={divStyle} alignItems="start">
            {(label || helpLabel) && (
                <Stack direction="column" maxWidth="100%">
                    <InputLabel
                        error={hasError}
                        disabled={props.disabled}
                        sx={{ whiteSpace: "normal", overflow: "visible", textOverflow: "clip" }}
                    >
                        {label}
                        {optional && (
                            <span data-testid="option-span" style={{ fontFamily: "Inter-Medium" }}>
                                {" "}
                                (Optional)
                            </span>
                        )}
                    </InputLabel>
                    <FormHelperText error={hasError} disabled={props.disabled} component="div">
                        {helpLabel}
                    </FormHelperText>
                </Stack>
            )}
            <OutlinedInput
                {...props}
                onChange={_onChange}
                type={type === "number" ? "text" : type || "text"}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={visibleValue}
                error={props.error || hasError}
            />
            {(labelButton || message || maxCharacters) && (
                <Stack alignItems="start" width="100%">
                    {labelButton}
                    <Stack direction="row" width="100%">
                        {message?.trim() && (
                            <FormHelperText error disabled={props.disabled} data-testid="error-message">
                                {message}
                            </FormHelperText>
                        )}
                        {!!maxCharacters && <CharacterCount value={value} maxCharacters={maxCharacters} />}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

const CharacterCount = ({ value, maxCharacters }: { value: string | number; maxCharacters: number }) => {
    const theme = useTheme();
    return (
        <Typography
            variant="caption"
            color={theme.palette.extendedGrey1.main}
            marginLeft="auto"
            lineHeight={2}
            data-testid="max-characters"
        >
            {`${value}`.length}/{maxCharacters}
        </Typography>
    );
};

/** use this for labelButton prop */
export const LabelButton = ({
    label = "Use default",
    onClick,
    disabled,
    ...props
}: IButtonProps & { label?: string }) => (
    <HButton
        aria-label="input-use-label-button"
        variant="text"
        size="small"
        onClick={onClick}
        disabled={disabled}
        data-testid="use-label-button"
        {...props}
    >
        {label}
    </HButton>
);

/** TODO Should this be replaced with LabelButton? */
export const HInputWithDefault = ({ onDefault, disabledWhen, ...props }: IInputWithDefaultProps) => {
    return (
        <Stack spacing={1} alignItems="start">
            <HInput {...props} divStyle={{ width: "100%" }} />
            <HButton
                aria-label="input-use-default-button"
                variant="text"
                size="small"
                onClick={onDefault}
                disabled={disabledWhen}
                data-testid="use-default-button"
            >
                Reset to default
            </HButton>
        </Stack>
    );
};

HInputWithDefault.displayName = "HInputWithDefault";
HInput.displayName = "HInput";

export default HInput;
